import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { api } from './api';

const AuthCtx = createContext(null);

// Token live: l'interceptor di axios legge da questa closure module-level.
// Eliminiamo così la race condition tra fetchDashboard() e l'effetto che
// registrava il token sull'interceptor.
let _currentToken = null;
// Esposto a moduli che fanno chiamate HTTP fuori da axios (es. fetch streaming).
export function getCurrentToken() {
  return _currentToken;
}
api.interceptors.request.use((cfg) => {
  if (_currentToken) cfg.headers.Authorization = `Bearer ${_currentToken}`;
  return cfg;
});

// Cleanup centralizzato: ripristina lo stato "non loggato" in modo idempotente
// e ricarica /login. Esposto a livello di modulo così l'interceptor 401 può
// invocarlo senza dipendere da React state.
let _setSessionRef = null;          // assegnato dall'AuthProvider al mount
let _signingOut = false;            // anti-loop sulle 401 multiple

function hardLogout(reason) {
  if (_signingOut) return;
  _signingOut = true;
  // Pulisci sessione Supabase (rimuove tutte le chiavi `sb-*-auth-token`).
  // CRUCIALE: altrimenti dopo il reload `supabase.auth.getSession()` ripesca
  // la sessione stale dal localStorage → _currentToken stale → 401 → LOOP.
  try {
    for (const k of Object.keys(localStorage)) {
      if (/^sb-.*-auth-token$/.test(k)) {
        localStorage.removeItem(k);
      }
    }
  } catch (e) {
    // localStorage può essere disabilitato (private mode / quota piena):
    // procediamo comunque al logout, la sessione lato API è già invalidata.
    if (process.env.NODE_ENV !== 'production') console.debug('[hardLogout] localStorage clear failed:', e);
  }
  // Best-effort async signOut (per invalidare server-side se possibile)
  try {
    supabase.auth.signOut().catch((e) => {
      if (process.env.NODE_ENV !== 'production') console.debug('[hardLogout] supabase.signOut rejected:', e);
    });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.debug('[hardLogout] supabase.signOut threw:', e);
  }
  _currentToken = null;
  if (_setSessionRef) _setSessionRef(null);
  // Forza un reload pulito su /login.
  try {
    if (!window.location.pathname.startsWith('/login')) {
      window.location.replace(`/login${reason ? `?reason=${reason}` : ''}`);
    }
  } catch (e) {
    // window.location può essere bloccato in iframe cross-origin: l'utente
    // resterà sulla pagina ma _currentToken è già null → niente più chiamate.
    if (process.env.NODE_ENV !== 'production') console.debug('[hardLogout] redirect failed:', e);
  }
}

// Interceptor risposte: se il backend ritorna 401, la sessione è scaduta
// (es. logout su un altro tab, refresh-token marcio). Ripuliamo e mandiamo
// a /login. Le route pubbliche (/login) restano illese perché non chiamano
// endpoint protetti.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    if (status === 401 && _currentToken) {
      hardLogout('expired');
    }
    return Promise.reject(err);
  },
);

export function AuthProvider({ children }) {
  // Sessione "unificata": viene da Supabase (Native Google OAuth o email/password).
  // shape: { access_token, user: { id, email, provider } }
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Espone setSession a livello modulo per hardLogout (401 interceptor + cross-tab)
  // e installa il sync cross-tab via 'storage' event.
  // Mount-only: setSession/_setSessionRef sono stabili. Niente deps necessarie.
  useEffect(() => {
    _setSessionRef = setSession;
    const onStorage = (e) => {
      // Supabase v2 salva la sessione in 'sb-<project>-auth-token' (o simile).
      // Se la chiave Supabase viene azzerata in un altro tab, hardLogout.
      if (e.key && /^sb-.*-auth-token$/.test(e.key) && (e.newValue === null || e.newValue === 'null')) {
        hardLogout('cross-tab');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      _setSessionRef = null;
    };
  }, []);

  // Bootstrap della sessione Supabase: una sola volta al mount del provider.
  // setSession e setLoading sono stable references da useState.
  useEffect(() => {
    let alive = true;

    // Sessione Supabase (Native Google OAuth + email/password)
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      _currentToken = data.session?.access_token || null;
      if (data.session) {
        setSession({
          access_token: data.session.access_token,
          user: {
            id: data.session.user?.id,
            email: data.session.user?.email,
            name: data.session.user?.user_metadata?.name,
            picture: data.session.user?.user_metadata?.avatar_url
              || data.session.user?.user_metadata?.picture,
            provider: 'supabase',
          },
        });
      }
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      _currentToken = s?.access_token || null;
      if (s) {
        setSession({
          access_token: s.access_token,
          user: {
            id: s.user?.id,
            email: s.user?.email,
            name: s.user?.user_metadata?.name,
            picture: s.user?.user_metadata?.avatar_url
              || s.user?.user_metadata?.picture,
            provider: 'supabase',
          },
        });
      } else {
        setSession(null);
      }
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    // hardLogout pulisce localStorage Supabase e reindirizza a /login.
    hardLogout();
  };

  const value = {
    session,
    user: session?.user || null,
    loading,
    signInEmail: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signUpEmail: (email, password) => supabase.auth.signUp({ email, password }),
    signOut,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
